/**
 * Turn-by-turn inside CivilHound (no Google tab): asks the server for the road route (Esri routing), then follows
 * your GPS — the next turn and how far to it, spoken out loud, a new route if you go off it, and "arrived".
 */
import { useEffect, useRef, useState } from 'react';
import { distanceFeet } from '@civilos/domain';
import { api } from '../shared/api.ts';

type LatLng = { lat: number; lng: number };
export interface RouteStep {
  text: string;
  miles: number;
  minutes: number;
  lat: number;
  lng: number;
  kind: string;
}
export interface DrivingRoute {
  miles: number;
  minutes: number;
  path: LatLng[];
  steps: RouteStep[];
}

const FT_PER_MI = 5280;
const ARRIVE_FT = 150;
const PASS_FT = 90;
const OFF_ROUTE_FT = 450;

/** "500 feet", "0.3 miles", "2 miles". */
export function spokenDistance(ft: number): string {
  if (ft < 1000) return `${Math.max(100, Math.round(ft / 100) * 100)} feet`;
  const mi = ft / FT_PER_MI;
  return mi < 10 ? `${(Math.round(mi * 10) / 10).toString()} miles` : `${Math.round(mi)} miles`;
}
export function shortDistance(ft: number): string {
  if (ft < 1000) return `${Math.max(50, Math.round(ft / 50) * 50)} ft`;
  const mi = ft / FT_PER_MI;
  return `${mi < 10 ? (Math.round(mi * 10) / 10).toFixed(1) : Math.round(mi)} mi`;
}

/** Feet from a point to the nearest part of the route line. */
function feetFromPath(p: LatLng, path: LatLng[]): number {
  if (path.length < 2) return path.length ? distanceFeet(p, path[0]!) : Infinity;
  const kx = Math.cos((p.lat * Math.PI) / 180) * 364_000;
  const ky = 364_000;
  let best = Infinity;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const ax = (a.lng - p.lng) * kx;
    const ay = (a.lat - p.lat) * ky;
    const bx = (b.lng - p.lng) * kx;
    const by = (b.lat - p.lat) * ky;
    const dx = bx - ax;
    const dy = by - ay;
    const len = dx * dx + dy * dy;
    const t = len ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len)) : 0;
    best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy));
  }
  return best;
}

const VOICE_KEY = 'civilos.navVoice';
function savedVoice(): string {
  try {
    return localStorage.getItem(VOICE_KEY) ?? '';
  } catch {
    return '';
  }
}
/** The voices this device has (English first), as the browser lists them. */
export function useVoices(): SpeechSynthesisVoice[] {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  useEffect(() => {
    if (typeof speechSynthesis === 'undefined') return;
    const load = () => setVoices(speechSynthesis.getVoices().filter((v) => /^en/i.test(v.lang)));
    load();
    speechSynthesis.addEventListener?.('voiceschanged', load);
    return () => speechSynthesis.removeEventListener?.('voiceschanged', load);
  }, []);
  return voices;
}

function speak(text: string, muted: boolean) {
  if (muted || typeof speechSynthesis === 'undefined') return;
  try {
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    const want = savedVoice();
    const v = want ? speechSynthesis.getVoices().find((x) => x.voiceURI === want || x.name === want) : null;
    if (v) {
      u.voice = v;
      u.lang = v.lang;
    }
    speechSynthesis.speak(u);
  } catch {
    /* no voice on this device */
  }
}

/** Pick the voice for directions (saved on this device) and hear it. */
export function VoicePicker() {
  const voices = useVoices();
  const [pick, setPick] = useState(savedVoice);
  if (!voices.length) return null;
  const choose = (id: string) => {
    setPick(id);
    try {
      localStorage.setItem(VOICE_KEY, id);
    } catch {
      /* ignore */
    }
    speak('In 500 feet, turn right on Main Street', false);
  };
  return (
    <select className="tbt-voice" aria-label="Voice for directions" value={pick} onChange={(e) => choose(e.target.value)}>
      <option value="">Default voice</option>
      {voices.map((v) => (
        <option key={v.voiceURI} value={v.voiceURI}>
          {v.name.replace(/^Microsoft\s+/, '').replace(/\s+Online \(Natural\)/, ' (natural)').replace(/ - English.*$/, '')}
        </option>
      ))}
    </select>
  );
}

/** Tidy Esri's wording for speaking ("Finish at Location 2, on the right" → "Your stop is on the right"). */
function sayable(s: RouteStep): string {
  if (s.kind === 'arrive') return /on the (left|right)/i.test(s.text) ? `Your stop is ${/on the left/i.test(s.text) ? 'on the left' : 'on the right'}` : 'You have arrived';
  return s.text.replace(/\bLocation \d+\b/g, 'your stop');
}

export interface Nav {
  to: LatLng;
  label: string;
  route: DrivingRoute | null;
  /** Index of the next maneuver. */
  next: number;
  /** Feet to the next maneuver. */
  feet: number | null;
  remainingMiles: number | null;
  arrived: boolean;
  loading: boolean;
  error: string | null;
}

export function useTurnByTurn(pos: (LatLng & { heading?: number | null }) | null) {
  const [nav, setNav] = useState<Nav | null>(null);
  const [muted, setMuted] = useState(false);
  const said = useRef(new Set<string>());
  const offCount = useRef(0);
  const lastRoute = useRef(0);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const fetchRoute = async (from: LatLng, to: LatLng, label: string, reroute = false) => {
    lastRoute.current = Date.now();
    setNav((n) => ({ ...(n ?? { route: null, next: 1, feet: null, remainingMiles: null, arrived: false, error: null }), to, label, loading: true, error: null }));
    try {
      const route = await api.post<DrivingRoute>('/api/route-demo/directions', { from, to });
      said.current = new Set();
      offCount.current = 0;
      setNav({ to, label, route, next: Math.min(1, route.steps.length - 1), feet: null, remainingMiles: route.miles, arrived: false, loading: false, error: null });
      const first = route.steps[1] ?? route.steps[0];
      if (first) {
        const ft = distanceFeet(from, first);
        speak(`${reroute ? 'New route. ' : ''}${ft > 400 ? `In ${spokenDistance(ft)}, ` : ''}${sayable(first)}`, mutedRef.current);
        said.current.add(ft <= 400 ? 'near:1' : 'far:1');
      }
    } catch (e) {
      setNav((n) => (n ? { ...n, loading: false, error: (e as Error).message } : n));
    }
  };

  const start = (to: LatLng, label: string) => {
    if (!pos) {
      setNav({ to, label, route: null, next: 1, feet: null, remainingMiles: null, arrived: false, loading: false, error: 'Waiting for your location (GPS) before directions can start.' });
      return;
    }
    void fetchRoute({ lat: pos.lat, lng: pos.lng }, to, label);
  };
  const stop = () => {
    setNav(null);
    try {
      speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
  };

  // Follow along as the GPS moves.
  useEffect(() => {
    if (!nav?.route || !pos || nav.loading) return;
    const r = nav.route;
    const toDest = distanceFeet(pos, nav.to);
    // Arrived at the house, or at the spot on the road where the route ends (a house set back on a long drive).
    const end = r.path[r.path.length - 1];
    if (!nav.arrived && (toDest < ARRIVE_FT || (end && distanceFeet(pos, end) < 120))) {
      const last = r.steps[r.steps.length - 1];
      speak(last ? sayable(last) : 'You have arrived', mutedRef.current);
      setNav({ ...nav, arrived: true, feet: 0, remainingMiles: 0, next: r.steps.length - 1 });
      return;
    }
    if (nav.arrived) return;
    // Off the route for a few fixes in a row → a new route from here (not more than every 20 s).
    if (feetFromPath(pos, r.path) > OFF_ROUTE_FT) {
      offCount.current++;
      if (offCount.current >= 3 && Date.now() - lastRoute.current > 20_000) {
        void fetchRoute({ lat: pos.lat, lng: pos.lng }, nav.to, nav.label, true);
        return;
      }
    } else offCount.current = 0;
    let next = nav.next;
    while (next < r.steps.length - 1 && distanceFeet(pos, r.steps[next]!) < PASS_FT) next++;
    const step = r.steps[next]!;
    const feet = distanceFeet(pos, step);
    // Spoken once at about half a mile out (if the leg is long), and again just before the turn.
    if (feet < 3000 && feet > 1200 && !said.current.has(`far:${next}`) && step.kind !== 'arrive') {
      said.current.add(`far:${next}`);
      speak(`In ${spokenDistance(feet)}, ${sayable(step)}`, mutedRef.current);
    } else if (feet <= 400 && !said.current.has(`near:${next}`)) {
      said.current.add(`near:${next}`);
      speak(sayable(step), mutedRef.current);
    }
    const remainingMiles = Math.round(((feet + r.steps.slice(next).reduce((t, s) => t + s.miles * FT_PER_MI, 0)) / FT_PER_MI) * 10) / 10;
    if (next !== nav.next || Math.abs((nav.feet ?? 0) - feet) > 20 || remainingMiles !== nav.remainingMiles) setNav({ ...nav, next, feet, remainingMiles });
  }, [pos?.lat, pos?.lng, nav]);

  return { nav, start, stop, muted, setMuted };
}

const ARROWS: Record<string, string> = {
  left: 'M15 19V11H7l3-3m-3 3 3 3',
  right: 'M9 19V11h8l-3-3m3 3-3 3',
  'bear-left': 'M15 19v-6L8 6m0 0v5m0-5h5',
  'bear-right': 'M9 19v-6l7-7m0 0v5m0-5h-5',
  'sharp-left': 'M15 5v8l-7 5m0 0h5m-5 0v-5',
  'sharp-right': 'M9 5v8l7 5m0 0h-5m5 0v-5',
  uturn: 'M15 19V9a4 4 0 0 0-8 0v6m0 0-3-3m3 3 3-3',
  straight: 'M12 20V5m0 0-4 4m4-4 4 4',
  depart: 'M12 20V5m0 0-4 4m4-4 4 4',
  roundabout: 'M12 20v-5m0-6a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 0V4m0 0-3 3m3-3 3 3',
  arrive: 'M12 21s6-6.3 6-11a6 6 0 0 0-12 0c0 4.7 6 11 6 11Zm0-9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
};

export function NavBanner({ nav, muted, onMute, onEnd, onAttempt, googleUrl }: { nav: Nav; muted: boolean; onMute: () => void; onEnd: () => void; onAttempt: () => void; googleUrl: string }) {
  const r = nav.route;
  const step = r?.steps[nav.next];
  const then = r && !nav.arrived ? r.steps[nav.next + 1] : null;
  return (
    <div className="tbt" role="region" aria-label="Directions" aria-live="polite">
      {nav.loading && !r ? (
        <div className="tbt-main">
          <div className="tbt-text">Getting directions to {nav.label}…</div>
        </div>
      ) : nav.error && !r ? (
        <div className="tbt-main bad">
          <div className="tbt-text">
            {nav.error}{' '}
            <a href={googleUrl} target="_blank" rel="noreferrer">
              Use Google Maps instead
            </a>
          </div>
        </div>
      ) : nav.arrived ? (
        <div className="tbt-main ok">
          <svg className="tbt-arrow" viewBox="0 0 24 24" aria-hidden="true">
            <path d={ARROWS.arrive} />
          </svg>
          <div className="tbt-text">
            <strong>Arrived</strong>
            <div>{nav.label}</div>
          </div>
          <button type="button" className="btn primary" onClick={onAttempt}>
            Attempted
          </button>
        </div>
      ) : step ? (
        <div className="tbt-main">
          <svg className="tbt-arrow" viewBox="0 0 24 24" aria-hidden="true">
            <path d={ARROWS[step.kind] ?? ARROWS.straight} />
          </svg>
          <div className="tbt-text">
            {nav.feet != null && <div className="tbt-dist">{shortDistance(nav.feet)}</div>}
            <strong>{step.kind === 'arrive' ? `${nav.label}` : step.text.replace(/\bLocation \d+\b/g, 'your stop')}</strong>
            {then && <div className="tbt-then">Then: {then.kind === 'arrive' ? 'arrive' : then.text.replace(/\bLocation \d+\b/g, 'your stop')}</div>}
          </div>
        </div>
      ) : null}
      <div className="tbt-bar">
        <span>{nav.loading && r ? 'Finding a new route…' : r ? `${nav.remainingMiles ?? r.miles} mi · to ${nav.label}` : ''}</span>
        <span className="tbt-btns">
          {!muted && <VoicePicker />}
          <button type="button" className="btn sm" onClick={onMute} aria-pressed={muted}>
            {muted ? 'Voice off' : 'Voice on'}
          </button>
          <button type="button" className="btn sm" onClick={onEnd}>
            End
          </button>
        </span>
      </div>
    </div>
  );
}
